// SSE client for POST /api/ask.
//
// EventSource is GET-only and /api/ask is a POST with a JSON body, so we stream
// the response body with fetch + a reader and parse the SSE frames by hand.
// Event order from the server: ack → step* → chart? → delta* → answer (or error).

import type {
  AckEvent,
  Answer,
  ChartEvent,
  DeltaEvent,
  ErrorEvent,
  HistoryTurn,
  StepEvent,
} from '../types'
import { apiFetch } from './http'
import { flagUnauthorized } from './auth'
import { isAnswer } from './contracts'
import { chatLanguage, type ChatLang } from '../chatLanguage'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

export interface AskHandlers {
  onAck?: (ack: AckEvent) => void
  onStep?: (step: StepEvent) => void
  onChart?: (chart: ChartEvent) => void
  onDelta?: (delta: DeltaEvent) => void
  onAnswer?: (answer: Answer) => void
  onError?: (err: ErrorEvent) => void
}

export interface AskOptions extends AskHandlers {
  signal?: AbortSignal
  timeoutMs?: number
}

// Errors the client writes itself, in the question's language (MP-OS-193); the server's
// own texts already arrive in that language. English is the wording used before.
const MESSAGES = {
  unreadable: { en: 'The answer could not be read — please try again.', sv: 'Svaret kunde inte läsas – försök igen.' },
  unparsable: { en: 'The answer could not be parsed — try again.', sv: 'Svaret kunde inte tolkas – försök igen.' },
  failed: { en: 'Could not answer that — please try again.', sv: 'Kunde inte svara på det – försök igen.' },
  busy: { en: 'Too many questions at once — please wait a moment.', sv: 'För många frågor samtidigt – vänta en stund.' },
  // MP-OS-193: the chat is admin-only for now; a refused seat gets a calm, final message.
  refused: { en: 'The chat is not available for your account yet.', sv: 'Chatten är inte tillgänglig för ditt konto än.' },
  timeout: {
    en: 'The question took too long. Try a shorter period or fewer comparisons.',
    sv: 'Frågan tog för lång tid. Prova en kortare period eller färre jämförelser.',
  },
  ended: {
    en: 'The connection ended before the answer was complete. Please try again.',
    sv: 'Anslutningen bröts innan svaret var klart. Försök igen.',
  },
  stopped: { en: 'The question was stopped.', sv: 'Frågan stoppades.' },
  // The user pressed Stop (App's stopQuestion).
  userStopped: { en: 'Question stopped. You can try again.', sv: 'Frågan stoppades. Du kan försöka igen.' },
  dropped: {
    en: 'The connection dropped before the answer was complete. Please try again.',
    sv: 'Anslutningen föll bort innan svaret var klart. Försök igen.',
  },
} satisfies Record<string, Record<ChatLang, string>>

// The language of each running question, by its abort signal: the client's own guess until
// the server's decision arrives (X-Chat-Language, or `lang` on the ack), then the server's.
const decided = new WeakMap<AbortSignal, ChatLang>()

const isLang = (value: unknown): value is ChatLang => value === 'sv' || value === 'en'

// The message for a question the user stopped, in the same language as the client's other
// messages for that question (the server's decision once known).
export function stoppedMessage(signal: AbortSignal | undefined, question = '', history: HistoryTurn[] = []): string {
  const lang = (signal && decided.get(signal)) || chatLanguage(question, history)
  return MESSAGES.userStopped[lang]
}

const startFailed = (status: number, lang: ChatLang) => lang === 'sv'
  ? `Frågan kunde inte startas (HTTP ${status}). Försök igen.`
  : `Could not start the question (HTTP ${status}). Please try again.`

interface SseFrame {
  event: string
  data: string
}

function parseFrame(raw: string): SseFrame | null {
  let event = 'message'
  const dataLines: string[] = []
  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (dataLines.length === 0) return null
  return { event, data: dataLines.join('\n') }
}

// A well-formed answer frame: `answer` is a string and `chart`, when present, is an
// object. A malformed frame (truncated/garbled SSE) must be rejected at the boundary so
// it can't reach the renderer and crash the turn — it surfaces as an error instead.
function dispatch(frame: SseFrame, handlers: AskHandlers, turn: { lang: ChatLang }): boolean {
  const lang = () => turn.lang
  let payload: unknown
  try {
    payload = JSON.parse(frame.data)
  } catch {
    handlers.onError?.({ message: MESSAGES.unreadable[lang()] })
    return true
  }
  switch (frame.event) {
    case 'ack':
      if (isLang((payload as AckEvent)?.lang)) turn.lang = (payload as AckEvent).lang as ChatLang
      handlers.onAck?.(payload as AckEvent)
      break
    case 'step':
      handlers.onStep?.(payload as StepEvent)
      break
    case 'chart':
      handlers.onChart?.(payload as ChartEvent)
      break
    case 'delta':
      handlers.onDelta?.(payload as DeltaEvent)
      break
    case 'answer':
      if (isAnswer(payload)) handlers.onAnswer?.(payload)
      else handlers.onError?.({ message: MESSAGES.unparsable[lang()] })
      return true
    case 'error':
      handlers.onError?.({ message: typeof (payload as ErrorEvent)?.message === 'string'
        ? (payload as ErrorEvent).message : MESSAGES.failed[lang()] })
      return true
  }
  return false
}

export async function ask(
  question: string,
  history: HistoryTurn[],
  opts: AskOptions,
): Promise<void> {
  if (opts.signal?.aborted) return
  // The client's own decision is only a fallback before the server answers (e.g. HTTP
  // 429/403 before the stream starts); the server's decision replaces it (MP-OS-193).
  const turn = { lang: chatLanguage(question, history) }
  const remember = (lang: ChatLang) => {
    turn.lang = lang
    if (opts.signal) decided.set(opts.signal, lang)
  }
  remember(turn.lang)
  const controller = new AbortController()
  const cancel = () => controller.abort()
  opts.signal?.addEventListener('abort', cancel, { once: true })
  let timedOut = false
  const timer = setTimeout(() => { timedOut = true; controller.abort() }, opts.timeoutMs ?? 120_000)
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined
  let terminal = false
  const fail = (message: string) => {
    if (terminal || opts.signal?.aborted) return
    terminal = true
    opts.onError?.({ message })
  }
  try {
    const response = await apiFetch(`${API_BASE}/api/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, history }),
      signal: controller.signal,
    })
    const serverLang = response.headers?.get?.('X-Chat-Language')
    if (isLang(serverLang)) remember(serverLang)
    if (response.status === 401) flagUnauthorized()
    if (!response.ok || !response.body) {
      fail(response.status === 429 ? MESSAGES.busy[turn.lang]
        : response.status === 403 ? MESSAGES.refused[turn.lang]
          : startFailed(response.status, turn.lang))
      return
    }
    reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    const emit = (raw: string) => {
      if (terminal || controller.signal.aborted) return
      const frame = parseFrame(raw)
      if (frame) {
        terminal = dispatch(frame, opts, turn)
        remember(turn.lang)
      }
    }

    while (!terminal && !controller.signal.aborted) {
      const { value, done } = await reader.read()
      if (done) {
        buffer += decoder.decode()
        if (buffer.trim()) emit(buffer)
        break
      }
      buffer += decoder.decode(value, { stream: true })
      let separator: RegExpExecArray | null
      while (!terminal && (separator = /\r?\n\r?\n/.exec(buffer))) {
        const raw = buffer.slice(0, separator.index)
        buffer = buffer.slice(separator.index + separator[0].length)
        emit(raw)
      }
    }
    if (!terminal && !opts.signal?.aborted) fail(timedOut ? MESSAGES.timeout[turn.lang] : MESSAGES.ended[turn.lang])
  } catch (e) {
    if (!opts.signal?.aborted) fail(timedOut
      ? MESSAGES.timeout[turn.lang]
      : (e as Error).name === 'AbortError' ? MESSAGES.stopped[turn.lang] : MESSAGES.dropped[turn.lang])
  } finally {
    clearTimeout(timer)
    opts.signal?.removeEventListener('abort', cancel)
    await reader?.cancel().catch(() => undefined)
  }
}
